import { useTranslation } from "react-i18next";
import { Check, ClipboardList, X } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { LoadingStatus } from "../../sessions/models/LoadingStatus";
import { SchedulePlan } from "../models/SchedulePlan";
import {
  dismissPlan,
  selectConfirmStatus,
  selectSelectedTempIds,
  togglePlanAction,
} from "../assistant-schedule-slice";
import { confirmSchedulePlan } from "../slice-extensions/assistant-schedule-slice-extension";

interface SchedulePlanCardProps {
  plan: SchedulePlan;
}

/** The confirm-before-write review card: tick the changes to apply, then confirm. */
export function SchedulePlanCard({ plan }: SchedulePlanCardProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const selectedTempIds = useAppSelector(selectSelectedTempIds);
  const confirmStatus = useAppSelector(selectConfirmStatus);

  const isConfirming = confirmStatus === LoadingStatus.Loading;
  const selectedActions = plan.actions.filter((action) =>
    selectedTempIds.includes(action.tempId),
  );

  return (
    <div className="with-border rounded-2xl p-4 lg:p-6">
      <div className="mb-3 flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-95 text-primary-50 dark:bg-primary-30 dark:text-primary-80">
          <ClipboardList size={20} strokeWidth={2} />
        </span>
        <div className="min-w-0">
          <h2 className="text-lg font-semibold leading-tight">{t("assistant.plan.title")}</h2>
          <p className="mt-0.5 text-sm text-grey-50 dark:text-grey-70">{plan.summary}</p>
        </div>
      </div>

      <ul className="space-y-2">
        {plan.actions.map((action) => (
          <li key={action.tempId}>
            <label className="flex cursor-pointer items-start gap-3 rounded-lg p-2 hover:bg-grey-95 dark:hover:bg-primary-20">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-primary-40"
                checked={selectedTempIds.includes(action.tempId)}
                onChange={() => dispatch(togglePlanAction(action.tempId))}
                disabled={isConfirming}
              />
              <span className="text-sm">{action.description}</span>
            </label>
          </li>
        ))}
      </ul>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => dispatch(confirmSchedulePlan(selectedActions))}
          disabled={isConfirming || selectedActions.length === 0}
        >
          <Check size={18} strokeWidth={2.5} />
          <span>
            {isConfirming
              ? t("assistant.plan.applying")
              : t("assistant.plan.confirm", { count: selectedActions.length })}
          </span>
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => dispatch(dismissPlan())}
          disabled={isConfirming}
        >
          <X size={18} strokeWidth={2} />
          <span>{t("assistant.plan.dismiss")}</span>
        </button>
      </div>
    </div>
  );
}
